import ServiceUnavailableAlert from 'components/core/ServiceUnavailableAlert';
import StatusReceitaBadge, { corStatusReceita } from 'components/receita/StatusReceitaBadge';
import { format, isValid, parseISO } from 'date-fns';
import { extrairCorpoReceita } from 'pages/receita/extrairCorpoReceita';
import { generateReceitaPdf } from 'pages/receita/generateReceitaPdf';
import { ehReceitaLivre, ROTULO_RECEITA_LIVRE } from 'pages/receita/receitaLivre';
import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Col, Dropdown, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectDoctor, selectFunc } from 'redux/features/auth/authSlice';
import history from 'services/history';
import {
  atualizarControleReceita,
  getReceitasPaciente,
  solicitarPedidoReceita,
} from 'services/receita';
import useSWR from 'swr';
import { getMedicosAgenda, resolveUsuarioAgenda } from 'utils/agenda';
import { isInfrastructureError } from 'utils/apiErrors';
import { enviarArquivo, toWhatsappNumber } from 'utils/evolution';
import { baixarBlobPdf, blobToBase64 } from 'utils/pdfBlob';

const STATUS_FIXOS = ['Solicitada', 'Prescrita', 'Entregue', 'Apanhada', 'Baixada', 'Cancelada'];

function parseData(valor) {
  if (!valor) return null;
  const data = typeof valor === 'string' ? parseISO(valor) : new Date(valor);
  return isValid(data) ? data : null;
}

function formatarDataHora(valor) {
  const data = parseData(valor);
  if (!data) return valor ? String(valor) : '—';
  return format(data, 'dd/MM/yyyy HH:mm');
}

function mascararCpf(cpf = '') {
  const digits = String(cpf).replace(/\D/g, '');
  if (digits.length !== 11) return String(cpf || '').trim() || '—';
  return `${digits.slice(0, 3)}.***.***-${digits.slice(9)}`;
}

function textoObservacao(receita) {
  const obs = String(receita?.OBS || '').trim();
  if (obs) return obs;
  return resumoReceita(receita?.TEXTO);
}

function resumoReceita(texto) {
  const linha = extrairCorpoReceita(texto || '')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .find((item) => item.length > 0);

  if (!linha) return '—';
  return linha.length > 80 ? `${linha.slice(0, 77)}...` : linha;
}

function statusReceita(receita) {
  return String(receita?.STATUS || '').trim();
}

function rascunho(receita) {
  return String(receita?.GRAVACAO || '').toUpperCase() === 'PARCIAL';
}

function temTextoReceita(receita) {
  return String(receita?.TEXTO || '').trim().length > 0;
}

/** Legado grava o controle com GRAVACAO nula. Só o rascunho parcial do receituário fica bloqueado. */
function podeImprimir(receita) {
  return !rascunho(receita) && temTextoReceita(receita);
}

function motivoImpressao(receita) {
  if (rascunho(receita)) {
    return 'Receita em rascunho. Grave como definitivo no receituário para imprimir.';
  }
  if (!temTextoReceita(receita)) return 'Pedido sem texto de receita.';
  return 'Gerar PDF timbrado';
}

function dataCurta(valor, formatada) {
  if (formatada) return formatada;
  const data = parseData(valor);
  if (!data) return '—';
  return format(data, 'dd/MM/yyyy');
}

function AcoesReceita({ receita, onVer, onImprimir, onWhatsapp, onControle, acaoAtual }) {
  const status = statusReceita(receita);
  const imprimir = podeImprimir(receita);
  const temCelular = Boolean(String(receita?.CELULARPAC || '').trim());
  const ocupado = Boolean(acaoAtual);
  const encerrada = status === 'Cancelada' || status === 'Baixada' || status === 'Apanhada';
  const livre = ehReceitaLivre(receita);
  const tituloWhatsapp = livre
    ? 'Receita livre não possui validação digital e não é enviada pelo WhatsApp.'
    : !imprimir
      ? motivoImpressao(receita)
      : temCelular
        ? 'Enviar PDF pelo WhatsApp'
        : 'Paciente sem celular cadastrado';

  return (
    <Dropdown align="end">
      <Dropdown.Toggle size="sm" variant="outline-secondary" disabled={ocupado}>
        Ações
      </Dropdown.Toggle>
      <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
        <Dropdown.Item onClick={() => onVer(receita)}>Ver conteúdo</Dropdown.Item>
        <Dropdown.Item
          disabled={!imprimir}
          title={motivoImpressao(receita)}
          onClick={() => onImprimir(receita)}
        >
          Imprimir
        </Dropdown.Item>
        <Dropdown.Item
          disabled={livre || !imprimir || !temCelular}
          title={tituloWhatsapp}
          onClick={() => onWhatsapp(receita)}
        >
          WhatsApp
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item disabled={encerrada} onClick={() => onControle(receita, 'entregue')}>
          Receita entregue pelo médico
        </Dropdown.Item>
        <Dropdown.Item
          disabled={status === 'Cancelada' || status === 'Baixada'}
          onClick={() => onControle(receita, 'apanhada')}
        >
          Receita entregue ao paciente
        </Dropdown.Item>
        <Dropdown.Item disabled={encerrada} onClick={() => onControle(receita, 'cancelar')}>
          Cancelar pedido
        </Dropdown.Item>
        <Dropdown.Item
          disabled={status === 'Cancelada' || status === 'Baixada'}
          title={
            status === 'Baixada' || status === 'Cancelada'
              ? 'Receita encerrada. A observação não pode mais ser alterada.'
              : 'Editar observação'
          }
          onClick={() => onControle(receita, 'observacao')}
        >
          Observação
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}

function CelulaMarco({ valor, formatada, nome }) {
  return (
    <div>
      <div className="text-nowrap">{dataCurta(valor, formatada)}</div>
      {nome ? <div className="small text-muted">{nome}</div> : null}
    </div>
  );
}

function CelulaDispensacao({ receita }) {
  if (
    String(receita?.STATUS || '')
      .trim()
      .toUpperCase() !== 'BAIXADA'
  ) {
    return null;
  }

  return (
    <div className="small">
      <div className="fw-semibold">{receita.ORIGEMUPDATE || 'Farmácia não informada'}</div>
      <div>{formatarDataHora(receita.DATAUPDADE)}</div>
      <div className="text-muted">CPF {mascararCpf(receita.CPFUPDATE)}</div>
    </div>
  );
}

function CelulaStatus({ receita }) {
  const rascunho = String(receita?.GRAVACAO || '').toUpperCase() === 'PARCIAL';

  return (
    <div className="d-flex flex-column align-items-center gap-1">
      <StatusReceitaBadge status={receita.STATUS} id={receita.CODRECEITA} />
      {rascunho && (
        <span className="badge text-bg-warning" title="Gravação parcial">
          Rascunho
        </span>
      )}
    </div>
  );
}

export default function ControleReceitasModal({ show, paciente, onHide }) {
  const medico = useSelector(selectDoctor);
  const func = useSelector(selectFunc);
  const { codFunc } = resolveUsuarioAgenda({ func, doctor: medico });
  const medicos = useMemo(() => getMedicosAgenda({ isFunc: Boolean(func) }), [func]);
  const codPac = paciente?.CODPAC;
  const [statusFiltro, setStatusFiltro] = useState('Todos');
  const [receitaAberta, setReceitaAberta] = useState(null);
  const [acaoAtual, setAcaoAtual] = useState(null);
  const [pedidoAberto, setPedidoAberto] = useState(false);
  const [obsPedido, setObsPedido] = useState('');
  const [codMedicoPedido, setCodMedicoPedido] = useState('');
  const [obsEdicao, setObsEdicao] = useState(null);

  useEffect(() => {
    if (!show || !codPac) {
      setReceitaAberta(null);
      return;
    }
    setStatusFiltro('Todos');
    setReceitaAberta(null);
    setPedidoAberto(false);
    setObsEdicao(null);
  }, [show, codPac]);

  const {
    data: receitas = [],
    error,
    isLoading,
    isValidating,
    mutate,
  } = useSWR(
    show && codPac ? ['receitas-controle', codPac] : null,
    ([, cod]) => getReceitasPaciente(cod),
    {
      revalidateOnFocus: false,
      shouldRetryOnError: true,
      errorRetryCount: 2,
      errorRetryInterval: 3000,
    }
  );

  const temReceitas = receitas.length > 0;
  const erroInfraestrutura = isInfrastructureError(error);

  const opcoesStatus = useMemo(() => {
    const extras = [
      ...new Set(
        receitas
          .map((receita) => String(receita.STATUS || '').trim())
          .filter((status) => status && !STATUS_FIXOS.includes(status))
      ),
    ];
    return ['Todos', ...STATUS_FIXOS, ...extras];
  }, [receitas]);

  const receitasFiltradas = useMemo(() => {
    return receitas.filter((receita) => {
      const status = String(receita.STATUS || '').trim();
      return statusFiltro === 'Todos' || status === statusFiltro;
    });
  }, [receitas, statusFiltro]);

  const handleNovaReceita = () => {
    if (!medico || !codPac) return;
    history.push(`/prontuario/${codPac}`, { aba: 'receita' });
  };

  const handleImprimir = async (receita) => {
    if (!podeImprimir(receita) || acaoAtual) return;

    setAcaoAtual({ cod: receita.CODRECEITA, tipo: 'pdf' });
    try {
      const blob = await generateReceitaPdf({ receita });
      const fileName = `Receita-Timbrado-Prevencor-${receita.NOMEPAC ?? 'Paciente'}-${receita.CODRECEITA}.pdf`;
      const url = URL.createObjectURL(blob);
      const janela = window.open(url, '_blank');
      if (!janela) {
        baixarBlobPdf({ blob, fileName });
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60000);
      toast.success('PDF da receita gerado.');
    } catch {
      toast.error('Erro ao gerar o PDF da receita.');
    } finally {
      setAcaoAtual(null);
    }
  };

  const handleWhatsapp = async (receita) => {
    if (ehReceitaLivre(receita)) {
      toast.warn('Receita livre não possui validação digital e não é enviada pelo WhatsApp.');
      return;
    }
    if (!podeImprimir(receita) || acaoAtual) return;

    const celularWhats = toWhatsappNumber(receita?.CELULARPAC, '');
    if (!celularWhats) {
      toast.warn('Celular do paciente não cadastrado ou inválido.');
      return;
    }

    setAcaoAtual({ cod: receita.CODRECEITA, tipo: 'whatsapp' });
    try {
      const blob = await generateReceitaPdf({ receita });
      const pdfBase64 = await blobToBase64(blob);
      const result = await enviarArquivo({
        to: celularWhats,
        pdfBase64,
        paciente: {
          id: receita?.CODPAC,
          nome: receita?.NOMEPAC,
          medico: medico?.apelido,
        },
        codLaudo: receita?.CODRECEITA,
        fileName: `Receita_${receita.CODRECEITA}.pdf`,
        caption: `Olá *${receita?.NOMEPAC}*, tudo bem? 😊\n\nSegue em anexo a sua *Receita Médica*.\n\nQualquer dúvida, nossa equipe estará à disposição no setor de atendimento: (14) 3402-2700`,
      });
      toast.success(result.message);
    } catch (err) {
      toast.error(err?.response?.data?.message ?? err.message ?? 'Erro ao enviar.');
    } finally {
      setAcaoAtual(null);
    }
  };

  const executarControle = async (receita, acao, obs) => {
    if (acaoAtual) return;
    setAcaoAtual({ cod: receita.CODRECEITA, tipo: acao });
    try {
      const resultado = await atualizarControleReceita(receita.CODRECEITA, {
        acao,
        obs,
        codFunc: codFunc || null,
      });
      toast.success(resultado?.message || 'Receita atualizada.');
      setObsEdicao(null);
      await mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao atualizar a receita.');
    } finally {
      setAcaoAtual(null);
    }
  };

  const handleControle = (receita, acao) => {
    if (acao === 'observacao') {
      setObsEdicao({ receita, texto: receita.OBS || '' });
      return;
    }
    if (acao === 'cancelar') {
      const confirmar = window.confirm('Cancelar este pedido de receita?');
      if (!confirmar) return;
    }
    executarControle(receita, acao);
  };

  const handleSolicitarPedido = async () => {
    if (!codPac || !codMedicoPedido || !obsPedido.trim() || acaoAtual) return;
    setAcaoAtual({ cod: 'pedido', tipo: 'pedido' });
    try {
      const resultado = await solicitarPedidoReceita(codPac, {
        codMedico: Number(codMedicoPedido),
        obs: obsPedido.trim(),
        codFunc: codFunc || null,
      });
      toast.success(resultado?.message || 'Pedido registrado.');
      setPedidoAberto(false);
      setObsPedido('');
      await mutate();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Erro ao registrar o pedido.');
    } finally {
      setAcaoAtual(null);
    }
  };

  const nomePaciente = paciente?.NOMEPAC || 'Paciente';

  return (
    <>
      <Modal show={show} onHide={onHide} size="xl" fullscreen="md-down" scrollable>
        <Modal.Header closeButton closeVariant="white" className="bg-primary text-white">
          <Modal.Title className="fs-5">Controle de Receitas — {nomePaciente}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Row className="g-2 align-items-end mb-3">
            <Col xs={12} md={4}>
              <Form.Label className="small mb-1">Status</Form.Label>
              <Form.Select
                value={statusFiltro}
                onChange={(event) => setStatusFiltro(event.target.value)}
              >
                {opcoesStatus.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
            <div className="d-flex flex-wrap gap-2">
              {STATUS_FIXOS.map((status) => (
                <StatusReceitaBadge key={status} status={status} id={`legenda-${status}`} />
              ))}
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => {
                  setObsPedido('');
                  setCodMedicoPedido(medicos[0] ? String(medicos[0].codMed) : '');
                  setPedidoAberto(true);
                }}
              >
                Novo pedido
              </Button>
              {medico && (
                <Button variant="primary" size="sm" onClick={handleNovaReceita}>
                  Nova receita
                </Button>
              )}
            </div>
          </div>

          {isLoading && !temReceitas ? (
            <div className="text-center py-4">
              <Spinner animation="border" variant="primary" role="status" />
              <p className="mt-2 text-muted small mb-0">Buscando receitas...</p>
            </div>
          ) : (
            <>
              {error &&
                (erroInfraestrutura ? (
                  <ServiceUnavailableAlert
                    error={error}
                    stale={temReceitas}
                    onRetry={() => mutate()}
                  />
                ) : (
                  <Alert variant="danger">
                    <p className="mb-0">
                      {error?.response?.data?.message ||
                        error?.message ||
                        'Erro ao carregar receitas.'}
                    </p>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      className="mt-2"
                      onClick={() => mutate()}
                    >
                      Tentar novamente
                    </Button>
                  </Alert>
                ))}

              {(!error || temReceitas) && receitasFiltradas.length === 0 && (
                <Alert variant="warning" className="text-center mb-0">
                  {temReceitas
                    ? 'Nenhuma receita com esse status.'
                    : 'Nenhuma receita encontrada para este paciente.'}
                </Alert>
              )}

              {receitasFiltradas.length > 0 && (
                <>
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">
                      {receitasFiltradas.length} de {receitas.length}
                    </small>
                    {isValidating && <Spinner animation="border" size="sm" variant="primary" />}
                  </div>

                  <div className="d-md-none d-flex flex-column gap-2">
                    {receitasFiltradas.map((receita) => (
                      <div
                        key={receita.CODRECEITA}
                        className="border rounded p-2"
                        style={{ borderLeft: `4px solid ${corStatusReceita(receita.STATUS)}` }}
                      >
                        <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                          <div>
                            <div className="fw-semibold">
                              {receita.NOMEMED || 'Médico não informado'}
                            </div>
                            <div className="small text-muted">
                              {ehReceitaLivre(receita)
                                ? ROTULO_RECEITA_LIVRE
                                : receita.TIPORECEITA || 'Receita'}
                            </div>
                          </div>
                          <CelulaStatus receita={receita} />
                        </div>
                        <div className="small mb-2">
                          Pedido {dataCurta(receita.DATAPEDIDO, receita.dataPedidoFormatada)}
                          {receita.NOMEFUNCPEDIDO ? ` · ${receita.NOMEFUNCPEDIDO}` : ''}
                          <br />
                          Entrega {dataCurta(receita.DATAENTREGA, receita.dataEntregaFormatada)}
                          {receita.NOMEFUNCENTREGA ? ` · ${receita.NOMEFUNCENTREGA}` : ''}
                          <br />
                          Apanha {dataCurta(receita.DATAAPANHA, receita.dataApanhaFormatada)}
                          {receita.NOMEFUNCAPANHA ? ` · ${receita.NOMEFUNCAPANHA}` : ''}
                        </div>
                        <div className="small mb-2">{textoObservacao(receita)}</div>
                        <CelulaDispensacao receita={receita} />
                        <div className="mt-2">
                          <AcoesReceita
                            receita={receita}
                            onVer={setReceitaAberta}
                            onImprimir={handleImprimir}
                            onWhatsapp={handleWhatsapp}
                            onControle={handleControle}
                            acaoAtual={acaoAtual}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="d-none d-md-block">
                    <Table responsive bordered hover size="sm" className="align-middle mb-0">
                      <thead className="table-light text-center">
                        <tr>
                          <th>Pedido</th>
                          <th>Entrega</th>
                          <th>Apanha</th>
                          <th>Médico</th>
                          <th>Status</th>
                          <th className="text-start">Obs</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {receitasFiltradas.map((receita) => (
                          <tr key={receita.CODRECEITA}>
                            <td>
                              <CelulaMarco
                                valor={receita.DATAPEDIDO}
                                formatada={receita.dataPedidoFormatada}
                                nome={receita.NOMEFUNCPEDIDO}
                              />
                            </td>
                            <td>
                              <CelulaMarco
                                valor={receita.DATAENTREGA}
                                formatada={receita.dataEntregaFormatada}
                                nome={receita.NOMEFUNCENTREGA}
                              />
                            </td>
                            <td>
                              <CelulaMarco
                                valor={receita.DATAAPANHA}
                                formatada={receita.dataApanhaFormatada}
                                nome={receita.NOMEFUNCAPANHA}
                              />
                            </td>
                            <td>{receita.NOMEMED || '—'}</td>
                            <td className="text-center">
                              <CelulaStatus receita={receita} />
                            </td>
                            <td className="small">
                              {textoObservacao(receita)}
                              <CelulaDispensacao receita={receita} />
                            </td>
                            <td className="text-center">
                              <AcoesReceita
                                receita={receita}
                                onVer={setReceitaAberta}
                                onImprimir={handleImprimir}
                                onWhatsapp={handleWhatsapp}
                                onControle={handleControle}
                                acaoAtual={acaoAtual}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!receitaAberta} onHide={() => setReceitaAberta(null)} centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            Receita #{receitaAberta?.CODRECEITA}
            {receitaAberta?.DATARECEITA ? ` — ${formatarDataHora(receitaAberta.DATARECEITA)}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <pre className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '14px' }}>
            {receitaAberta?.TEXTO || 'Sem conteúdo.'}
          </pre>
        </Modal.Body>
      </Modal>

      <Modal show={pedidoAberto} onHide={() => setPedidoAberto(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">Novo pedido de receita</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Médico</Form.Label>
            <Form.Select
              value={codMedicoPedido}
              onChange={(event) => setCodMedicoPedido(event.target.value)}
            >
              <option value="">Selecione</option>
              {medicos.map((item) => (
                <option key={item.codMed} value={item.codMed}>
                  {item.apelido || item.nome}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Observação</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              maxLength={250}
              value={obsPedido}
              onChange={(event) => setObsPedido(event.target.value)}
              placeholder="Ex.: receita, pedido de exames"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setPedidoAberto(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleSolicitarPedido}
            disabled={!codMedicoPedido || !obsPedido.trim() || Boolean(acaoAtual)}
          >
            Registrar pedido
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!obsEdicao} onHide={() => setObsEdicao(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="fs-6">
            Observação da receita #{obsEdicao?.receita?.CODRECEITA}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Control
            as="textarea"
            rows={3}
            maxLength={250}
            value={obsEdicao?.texto || ''}
            onChange={(event) =>
              setObsEdicao((atual) => (atual ? { ...atual, texto: event.target.value } : atual))
            }
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setObsEdicao(null)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={!String(obsEdicao?.texto || '').trim() || Boolean(acaoAtual)}
            onClick={() =>
              executarControle(obsEdicao.receita, 'observacao', obsEdicao.texto.trim())
            }
          >
            Gravar
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
